import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, Button, Input, Label } from "@/components/ui";
import { useGetProfile, useGetUsage, useUpdateProfile } from "@workspace/api-client-react";
import { useAuth } from "@/lib/auth-context";
import { Loader2, CheckCircle2, Crown, AlertTriangle } from "lucide-react";
import { API_BASE } from "@/lib/api";

const updateSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").optional(),
  password: z.string().optional().refine(val => !val || val.length >= 6, "Password must be at least 6 characters if provided"),
});

type UpdateForm = z.infer<typeof updateSchema>;

const PLAN_LABELS: Record<string, string> = { free: "Free", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };

function apiCall(path: string, method: string, body?: object) {
  const token = localStorage.getItem("studyflow_token");
  return fetch(`${API_BASE.replace(/\/$/, "")}${path}`, {
    method,
    headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message ?? data.error ?? "Request failed");
    return data;
  });
}

export default function Settings() {
  const { data: profile, isLoading, refetch } = useGetProfile();
  const { data: usage, refetch: refetchUsage } = useGetUsage();
  const { user } = useAuth();

  const [success, setSuccess] = useState(false);

  // Cancel state
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<UpdateForm>({
    resolver: zodResolver(updateSchema),
    defaultValues: { name: user?.name || "" },
  });

  const { mutate: update, isPending } = useUpdateProfile({
    mutation: {
      onSuccess: () => {
        setSuccess(true);
        refetch();
        setTimeout(() => setSuccess(false), 3000);
      }
    }
  });

  const onSubmit = (data: UpdateForm) => {
    const payload: any = {};
    if (data.name && data.name !== user?.name) payload.name = data.name;
    if (data.password) payload.password = data.password;
    if (Object.keys(payload).length > 0) update({ data: payload });
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await apiCall("/api/subscription/cancel", "DELETE");
      setCancelSuccess(true);
      setCancelConfirm(false);
      refetchUsage();
    } catch (e: any) {
      setCancelError(e.message ?? "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  if (isLoading) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const currentPlan = usage?.plan ?? "free";
  const isPaidPlan = currentPlan !== "free";

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in">
      <div>
        <h1 className="text-3xl font-bold font-display">Account Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your profile and preferences.</p>
      </div>

      {/* Subscription Card */}
      <Card className="p-6 sm:p-8 space-y-5">
        <div className="flex items-center gap-3">
          <Crown className={`w-5 h-5 ${isPaidPlan ? "text-yellow-500" : "text-muted-foreground"}`} />
          <h2 className="text-lg font-semibold">Subscription</h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Current plan</p>
            <p className="text-2xl font-bold mt-0.5">{PLAN_LABELS[currentPlan] ?? currentPlan}</p>
          </div>
          {!isPaidPlan && (
            <a href="/dashboard">
              <Button variant="default" size="sm">Upgrade Plan</Button>
            </a>
          )}
        </div>

        {/* Cancel section — only for paid plans */}
        {isPaidPlan && !cancelSuccess && (
          <div className="pt-4 border-t border-border">
            {!cancelConfirm ? (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => { setCancelConfirm(true); setCancelError(null); }}
              >
                Cancel Subscription
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-sm text-destructive">
                    Are you sure? You'll be downgraded to Free immediately and lose access to unlimited study features.
                  </p>
                </div>
                {cancelError && <p className="text-sm text-destructive">{cancelError}</p>}
                <div className="flex gap-2">
                  <Button variant="destructive" size="sm" isLoading={cancelling} onClick={handleCancel}>
                    Yes, cancel my subscription
                  </Button>
                  <Button variant="outline" size="sm" disabled={cancelling} onClick={() => { setCancelConfirm(false); setCancelError(null); }}>
                    Keep my plan
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {cancelSuccess && (
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
            Subscription cancelled. You're now on the Free plan.
          </div>
        )}
      </Card>

      {/* Profile Card */}
      <Card className="p-6 sm:p-8">
        <h2 className="text-lg font-semibold mb-6">Profile</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="bg-muted text-muted-foreground cursor-not-allowed" />
            <p className="text-xs text-muted-foreground">Email cannot be changed.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" {...register("name")} />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">New Password (optional)</Label>
            <Input id="password" type="password" placeholder="Leave blank to keep current" {...register("password")} />
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>
          <div className="pt-4 flex items-center justify-between">
            {success ? (
              <span className="flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Profile updated successfully
              </span>
            ) : <span />}
            <Button type="submit" isLoading={isPending}>Save Changes</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
